import { apiUrl } from '@app/componentlib/src/application/helpers/api'
import store from '@app/componentlib/src/store'
import Vue from 'vue'
import { Action, Module, VuexModule, Mutation, getModule } from "vuex-module-decorators"
import { httpService } from '@app/componentlib/src/services/HttpService'
import { IJobPosting } from '@app/componentlib/common/types/JobSearchTypes'
import { JobMatchModule } from '../JobMatch/jobMatch'
import { AppModule } from '../app'

export interface ISavedAdvert {
  _savedAdverts: IJobPosting[] | undefined
  _count: number,
  _loading: boolean,
  //used for showing active job posting on JobDetailsView for Saved Adverts
  _selectedJobPosting: IJobPosting | undefined,
  //Active index is used on JobResultList for Saved Adverts
  _activeIndex: number,
  _disableSearchForMore: boolean,
}

const jobMatchApi = 'JobMatchApi'

@Module({
  dynamic: true,
  store,
  name: 'savedAdverts',
  namespaced: true
})

class savedAdvert extends VuexModule implements ISavedAdvert {
    _savedAdverts: IJobPosting[] | undefined;
    _count = 0;
    _loading = false;
    _selectedJobPosting: IJobPosting | undefined;
    _activeIndex = 0;
    _disableSearchForMore = false

    @Action({ rawError: true })
    async getSavedAdverts() {
      this.SET_LOADING(true)
      //Call backend to get saved adverts
      const reqUrl = apiUrl(jobMatchApi, 'GetSavedAdverts')

      await httpService.get(reqUrl).then((data: IJobPosting[]) => {
        if(data !== null) {
          this.SET_COUNT(data.length)
          //Save adverts to _savedAdverts
          this.SET_SAVEDADVERTS(data)

          //Job Posting opens in full screen on mobile, and we don't want that when creating saved advert
          if(AppModule.isMobile === false) {
            this.SET_SELECTEDJOBPOSTING(data[0])
          }

          this.SET_LOADING(false)
        }
      })
    }

    @Action({ rawError: true })
    async deleteSavedAdvert(jobId: number) {
      //Call backend to delete saved adverts
      const reqUrl = apiUrl(jobMatchApi, 'DeleteSavedAdvert')

      await httpService.delete(reqUrl, jobId).then((deleted: boolean) => {
        if(deleted === true) {
          var updatedSavedAdverts = this.savedAdverts?.filter((advert) => advert.JobId !== jobId)

          JobMatchModule.jobPostings?.forEach(posting => {
            if(posting.JobId === jobId) {
              posting.IsSavedAdvert = false;
            }
          });

          //This is used for deeplink advert because there we only have on advert
          if (jobId === JobMatchModule.advert?.JobId) {
            JobMatchModule.advert.IsSavedAdvert = false;
          }

          //Update _savedAdverts and savedAdverts count
          this.SET_SAVEDADVERTS(updatedSavedAdverts)

          this.SET_COUNT(this.count - 1)

          if(this.savedAdverts !== undefined) {
            //When use has selected the last saved advert in the list and removes it, it should select the saved advert above it. To avoid blank job details view
            if(this.savedAdverts[this.activeIndex] === undefined) {
              this.SET_SELECTEDJOBPOSTING(this.savedAdverts[this.activeIndex - 1])
              this.SET_ACTIVEINDEX(this.activeIndex - 1)
            } else {
              //When user removes saved advert thats not the last in the array, it should select the saved advert below it, to avoid job details not updating
              this.SET_SELECTEDJOBPOSTING(this.savedAdverts[this.activeIndex])
            }
          }

          //When no saved adverts is saved on the users profile, we need to reset selected job posting
          if(this.count === 0) {
            this.SET_SELECTEDJOBPOSTING(undefined)
          }
        }
      })
    }

    @Action({ rawError: true })
    async createSavedAdvert(jobId: number) {
      //Call backend to create saved advert
      const reqUrl = apiUrl(jobMatchApi, 'CreateSavedAdvert')
      await httpService.post(reqUrl,jobId).then((data: IJobPosting) => {
        if(data !== null) {
          var tempList = this.savedAdverts === undefined ? [] : this.savedAdverts
          tempList.unshift(data)
          this.SET_SAVEDADVERTS(tempList)

          JobMatchModule.jobPostings?.forEach(posting => {
            if(posting.JobId === jobId) {
              posting.IsSavedAdvert = true;
            }
          });

          //This is used for deeplink advert because there we only have on advert
          if (jobId === JobMatchModule.advert?.JobId) {
            JobMatchModule.advert.IsSavedAdvert = true;
          }

          //Job Posting opens in full screen on mobile, and we don't want that when creating saved advert
          if(AppModule.isMobile === false) {
            this.SET_SELECTEDJOBPOSTING(this.savedAdverts !== undefined ? this.savedAdverts[0] : data)
          }

          //Update _savedAdverts and savedAdverts count
          this.SET_COUNT(this.count + 1)
        }
      })
    }

    @Action({ rawError: true })
    setSelectedJobPosting(jobPosting: IJobPosting | undefined) {
      this.SET_SELECTEDJOBPOSTING(jobPosting)
    }

    @Action({rawError: true })
    async setActiveIndex(index: number) {
      this.SET_ACTIVEINDEX(index)
    }

    @Action({rawError: true })
    async setDisableSearchForMore(value: boolean) {
      this.SET_DISABLESEARCHFORMORE(value)
    }

   //Mutations
    @Mutation
    SET_SAVEDADVERTS (savedAdverts: IJobPosting[] | undefined) {
      Vue.set(this, '_savedAdverts', savedAdverts)
    }

    @Mutation
    SET_COUNT (count: number) {
      Vue.set(this, '_count', count)
    }

    @Mutation
    SET_LOADING (loading: boolean) {
      Vue.set(this, '_loading', loading)
    }

    @Mutation
    SET_SELECTEDJOBPOSTING(jobPosting: IJobPosting | undefined) {
        Vue.set(this, "_selectedJobPosting", jobPosting)
    }

    @Mutation
    SET_ACTIVEINDEX(index: number) {
      Vue.set(this, "_activeIndex", index)
    }

    @Mutation
    SET_DISABLESEARCHFORMORE(value: boolean) {
      Vue.set(this, "_disableSearchForMore", value)
    }

    // Getters
    get savedAdverts (): IJobPosting[] | undefined {
      return this._savedAdverts
    }

    get count (): number {
      return this._count
    }

    get loading(): boolean {
      return this._loading
    }

    get selectedJobPosting (): IJobPosting | undefined {
      return this._selectedJobPosting
    }

    get activeIndex (): number {
      return this._activeIndex
    }

    get disableSearchForMore (): boolean {
      return this._disableSearchForMore
    }
}

export const SavedAdvertsModule = getModule(savedAdvert)